import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '~/components/Button';
import { ErrorToast } from '~/components/ErrorToast/ErrorToast';
import { CTABlock } from '~/modules/settings/components/CTABlock';
import { SettingsPageHeader } from '~/modules/settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { UserFormSchema } from '~/modules/settings/modules/user/components/UserForm';
import {
  mapFormDataToApi,
  UserForm,
} from '~/modules/settings/modules/user/components/UserForm';
import type { UserDetail } from '~settings/api/lib/schemas';
import { useCreateUser } from '~settings/api/mutations/userCreate';
import { useToast } from '~settings/hooks/useToast';
import { getMessageFromStatus } from '~settings/modules/user/helpers';

const FORM_ID = 'createUserForm';

export function CreateUserPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  const { openToast, isToastOpen, toastContent, closeToast } = useToast();

  const { mutate: createUser, isPending } = useCreateUser({
    onSuccess: (data: UserDetail) => {
      navigate(SETTINGS_ROUTES.USER_DETAIL(data.id));
    },
    onError: (error) => {
      const status = error?.response?.status;
      const message = getMessageFromStatus(status);

      openToast({
        title: t(message.title),
        description: t(message.description),
      });
    },
  });

  const onSubmit = (data: UserFormSchema) => {
    const apiData = mapFormDataToApi(data);

    createUser(apiData);
  };

  return (
    <article className="relative flex min-h-screen flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.USER_LIST, label: 'users' },
          { to: '', label: 'user.createUserTitle' },
        ]}
        title="user.createUserTitle"
      />
      <div className="grow overflow-y-auto">
        <UserForm onSubmit={onSubmit} id={FORM_ID} />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.USER_LIST}>{t('cancelButton')}</Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          size="large"
          form={FORM_ID}
          disabled={isPending}
        >
          {t('user.addUserButton')}
        </Button>
      </CTABlock>
      <ErrorToast
        isOpen={isToastOpen}
        onClose={closeToast}
        {...toastContent}
        className="max-w-[19rem]"
      />
    </article>
  );
}

CreateUserPage.displayName = 'CreateUserPage';
